import {
  countSelectedWords,
  REPLACE_TERM_LIMIT_REASON,
  REPLACE_TERM_WORD_LIMIT,
} from '@src/utility/replaceTermLimit';
import { isCjkOpeningPunctuation } from '@src/utility/strings';
import {
  getClosestElementIndexToLeftByFilter,
  getClosestElementIndexToRightByFilter,
  getClosestNotRemovedElementIndexToLeft,
  getClosestNotRemovedElementIndexToRight,
  getClosestNotRemovedTextIndexToLeft,
  getClosestNotRemovedTextIndexToRight,
  getClosestRemovedIndexToLeft,
  getClosestRemovedIndexToRight,
  normalizeWordBoundaries,
} from '@src/utility/videoTranscriptionUtils';
import { type RefObject, useCallback, useState } from 'react';

import {
  getTranscriptElementFromCharIndex,
  isCaptionRowStart,
  isWhitespaceElement,
} from '../components/transcript/utils';
import { isElementReassigned } from '../hooks/useSpeakerOverrides';
import { SidebarOption } from '../types.ts/general';
import { AspectRatio } from '../types.ts/video';
import { extractErrorInfo } from '../utility/error';
import { LogManager } from '../utility/Logger';
import { dispatchTranscriptSeek } from '../utility/transcriptEvents';
import { useKaraoke } from './useKaraoke';

import type {
  TranscriptClipSaveService,
  TranscriptMediaService,
} from '../components/transcript/Transcript';
import type { TranscriptElementBlock } from '../components/transcript/utils';
import type { EditorStore } from '../editor/EditorStore';
import type { KaraokeLine } from '../editor/types/karaoke';
import type { ITranscriptVideoCreator } from '../stores/ITranscriptVideoCreator';
import type { DatoClientStore } from '../stores-v2/DatoClientStore';
import type {
  ImageKey,
  ImageWithType,
  TranscriptContextMenuActions,
  TranscriptContextMenuBuild,
  TranscriptContextMenuDisabledReasons,
} from '../types.ts/general';
import type { TranscriptElementWithViewData } from '../types.ts/transcript';
import type { ResolvedTranscriptSelection } from '@src/utility/videoTranscriptionUtils';

// Selection boundary shape (legacy index + optional segment addressing)
interface SelectionBoundaries {
  startIndex: number;
  endIndex: number;
  focusIndex?: number;
  anchorIndex?: number;
}

const logger = LogManager.getLogger('hook:useTranscriptOperations');

export const useTranscriptOperations = (
  videoCreator: ITranscriptVideoCreator,
  editorStore: EditorStore,
  datoClientStore: DatoClientStore,
  clipSaveService: TranscriptClipSaveService | undefined,
  mediaService: TranscriptMediaService | undefined,
  transcriptElements: TranscriptElementWithViewData[] | undefined,
  showHistory: boolean,
  transcriptBlocks: TranscriptElementBlock[],
  karaokeLines: KaraokeLine[] | undefined,
  hasKaraokeLines: boolean,
  /**
   * PLAT-10646 — programmatic selection override for the translated prose
   * view's context-menu actions. The translated DOM has no `data-index`
   * spans, so the DOM-selection resolvers below can't see a selection
   * there; the dispatcher in Transcript.tsx maps the clicked phrase back
   * to a source element range, sets it here for the duration of one
   * action, and clears it after. A ref (not state) because the handlers
   * read it synchronously mid-dispatch.
   */
  selectionRangeOverrideRef?: RefObject<{
    startIndex: number;
    endIndex: number;
  } | null>,
) => {
  const [selectedWordIndex, setSelectedWordIndex] = useState<number>(-1);

  const {
    commandAddBreak,
    commandRemoveBreak,
    commandToggleCaptionSoftBreak,
    commandHideInKaraoke,
    commandShowInKaraoke,
  } = useKaraoke(editorStore.editor);

  const compensateForRemovedElementsPosition = useCallback(
    (index: number) => {
      if (showHistory) return index;
      if (!transcriptElements) return 0;
      let i = 0;
      while (index > 0 && i < transcriptElements.length) {
        if (
          transcriptElements[i].state !== 'removed' &&
          transcriptElements[i].state !== 'cut'
        ) {
          index--;
        }
        i++;
      }
      return i;
    },
    [showHistory, transcriptElements],
  );

  const getBoundaryIndexesFromSelectionInDiff = useCallback(
    (selection: Selection) => {
      // Helper to find nearest ancestor with data-block-index attribute
      // This is needed because SpeakerHeader/TimestampRow elements don't have this attribute
      const findBlockIndex = (node: Node | null): number | null => {
        let element: HTMLElement | null =
          node instanceof HTMLElement ? node : (node?.parentElement ?? null);
        while (element) {
          if (element.dataset.blockIndex !== undefined) {
            return +element.dataset.blockIndex;
          }
          element = element.parentElement;
        }
        return null;
      };

      // Helper to find data-char-offset attribute for split blocks
      // This offset indicates where the split block's text starts within the original block
      const findCharOffset = (node: Node | null): number => {
        let element: HTMLElement | null =
          node instanceof HTMLElement ? node : (node?.parentElement ?? null);
        while (element) {
          if (element.dataset.charOffset !== undefined) {
            return +element.dataset.charOffset;
          }
          element = element.parentElement;
        }
        return 0;
      };

      // Compute the character offset relative to the block span (the ancestor
      // with data-block-index), not just the immediate text node.  When inner
      // <span> elements exist (multi-element blocks for search highlighting),
      // the browser selection offset is relative to the inner span's text node.
      // We need to sum preceding sibling text lengths to get the block-relative offset.
      const getBlockRelativeOffset = (
        node: Node | null,
        localOffset: number,
      ): number => {
        if (!node) return localOffset;
        // Walk up from the selection node to the block span, accumulating
        // text lengths of preceding siblings at each level.
        // Start from the node itself (not its parent) so that when a text node
        // is a direct child of the block span, its own preceding siblings are
        // counted before the loop hits the block span and breaks.
        let current: Node | null = node;
        let offset = localOffset;

        while (current) {
          if (
            current instanceof HTMLElement &&
            current.dataset.blockIndex !== undefined
          ) {
            // Reached the block span — offset is now block-relative
            break;
          }
          // Add text lengths of all preceding siblings
          let sibling = current.previousSibling;
          while (sibling) {
            offset += sibling.textContent?.length || 0;
            sibling = sibling.previousSibling;
          }
          current = current.parentNode;
        }
        return offset;
      };

      const anchorBlockIndex = findBlockIndex(selection.anchorNode);
      const focusBlockIndex = findBlockIndex(selection.focusNode);

      // If either anchor or focus couldn't be found (e.g., node landed in a
      // non-selectable SpeakerHeader/TimestampRow, or the browser placed the
      // selection on the parent container), use a Range-based fallback that
      // walks all block spans to find the first and last intersecting blocks.
      if (anchorBlockIndex === null || focusBlockIndex === null) {
        return getRangeBasedBoundaries(selection);
      }

      // Get character offsets for split blocks (0 for non-split blocks)
      const anchorCharOffset = findCharOffset(selection.anchorNode);
      const focusCharOffset = findCharOffset(selection.focusNode);

      // Determine if anchor comes before focus by comparing blockIndex first, then charOffset
      // This handles the case where split blocks have the same originalIndexOffset but different charOffset
      const anchorIsBeforeFocus =
        anchorBlockIndex < focusBlockIndex ||
        (anchorBlockIndex === focusBlockIndex &&
          anchorCharOffset <= focusCharOffset);

      const [startBlockIndex, endBlockIndex] = anchorIsBeforeFocus
        ? [anchorBlockIndex, focusBlockIndex]
        : [focusBlockIndex, anchorBlockIndex];

      // Calculate which charOffset corresponds to start vs end
      // This must be done before finding blocks so we can match split blocks correctly
      const [startCharOffset, endCharOffset] = anchorIsBeforeFocus
        ? [anchorCharOffset, focusCharOffset]
        : [focusCharOffset, anchorCharOffset];

      // Search by originalIndexOffset ?? indexOffset to match the DOM data-block-index attribute
      // Also match charOffset to find the correct split block when multiple exist
      const startBlock = transcriptBlocks.find(
        block =>
          (block.originalIndexOffset ?? block.indexOffset) ===
            startBlockIndex && (block.charOffset ?? 0) === startCharOffset,
      );
      const endBlock = transcriptBlocks.find(
        block =>
          (block.originalIndexOffset ?? block.indexOffset) === endBlockIndex &&
          (block.charOffset ?? 0) === endCharOffset,
      );
      // Determine start/end offsets based on selection direction
      // When in the same split block, sort by offset value
      // When in different split blocks (even with same originalIndexOffset), use the computed ordering
      const inSameSplitBlock =
        anchorBlockIndex === focusBlockIndex &&
        anchorCharOffset === focusCharOffset;

      // Compute block-relative offsets to handle inner span elements
      const anchorBlockRelativeOffset = getBlockRelativeOffset(
        selection.anchorNode,
        selection.anchorOffset,
      );
      const focusBlockRelativeOffset = getBlockRelativeOffset(
        selection.focusNode,
        selection.focusOffset,
      );

      const [startOffset, endOffset] = inSameSplitBlock
        ? [anchorBlockRelativeOffset, focusBlockRelativeOffset].sort(
            (a, b) => a - b,
          )
        : anchorIsBeforeFocus
          ? [anchorBlockRelativeOffset, focusBlockRelativeOffset]
          : [focusBlockRelativeOffset, anchorBlockRelativeOffset];

      // Note: We no longer add charOffset because we now find the correct split block directly
      // using charOffset matching. The selection offset is relative to the split block's span,
      // and we search within that split block's elements.

      const startElement = getTranscriptElementFromCharIndex(
        startOffset,
        startBlock?.elements,
      );
      const endElement = getTranscriptElementFromCharIndex(
        endOffset,
        endBlock?.elements,
      );

      if (startElement && endElement && startBlock && endBlock) {
        // Use the split block's indexOffset (global position) + element position within that block
        return {
          startIndex: startBlock.indexOffset + startElement.elementIndex,
          endIndex: endBlock.indexOffset + endElement.elementIndex,
        };
      }
      return null;
    },
    [transcriptBlocks],
  );

  // Fallback: when the DOM-based anchor/focus lookup fails (selection crosses
  // non-selectable elements like TimestampRow/SpeakerHeader, or browser placed
  // the selection on a container node), use the Range API to walk all block
  // spans and find the first/last ones that intersect the selection range.
  const getRangeBasedBoundaries = useCallback(
    (selection: Selection): SelectionBoundaries | null => {
      if (selection.rangeCount === 0) return null;
      const range = selection.getRangeAt(0);

      // Find the transcript container that holds all block spans
      const container = document.getElementById(
        'transcript-scrollable-container',
      );
      if (!container) return null;

      // Query all block spans and find the first/last ones in the selection range
      const blockSpans =
        container.querySelectorAll<HTMLElement>('[data-block-index]');
      let firstSpan: HTMLElement | null = null;
      let lastSpan: HTMLElement | null = null;

      for (const span of blockSpans) {
        if (range.intersectsNode(span)) {
          if (!firstSpan) firstSpan = span;
          lastSpan = span;
        }
      }

      if (!firstSpan || !lastSpan) return null;

      // Map the first and last spans to transcript block indices
      const firstBlockIndex = +(firstSpan.dataset.blockIndex || '0');
      const firstCharOffset = +(firstSpan.dataset.charOffset || '0');
      const lastBlockIndex = +(lastSpan.dataset.blockIndex || '0');
      const lastCharOffset = +(lastSpan.dataset.charOffset || '0');

      const firstBlock = transcriptBlocks.find(
        block =>
          (block.originalIndexOffset ?? block.indexOffset) ===
            firstBlockIndex && (block.charOffset ?? 0) === firstCharOffset,
      );
      const lastBlock = transcriptBlocks.find(
        block =>
          (block.originalIndexOffset ?? block.indexOffset) === lastBlockIndex &&
          (block.charOffset ?? 0) === lastCharOffset,
      );

      if (!firstBlock || !lastBlock) return null;

      // Use the start of the first block and end of the last block
      const startIndex = firstBlock.indexOffset;
      const lastElementIdx = lastBlock.elements.length - 1;
      const endIndex = lastBlock.indexOffset + Math.max(0, lastElementIdx);

      return { startIndex, endIndex };
    },
    [transcriptBlocks],
  );

  // Legacy: Index-based boundary extraction for VideoCreatorStore
  const getIndexBoundariesFromSelection = useCallback(
    (
      selection: Selection,
      scrollRef: RefObject<HTMLDivElement>,
    ): SelectionBoundaries | null => {
      // Helper to find nearest ancestor with data-index attribute
      // This is needed because SpeakerHeader/TimestampRow elements don't have this attribute
      const findDataIndex = (node: Node | null): number | null => {
        let element: HTMLElement | null =
          node instanceof HTMLElement ? node : (node?.parentElement ?? null);
        while (element && element !== scrollRef.current) {
          if (element.dataset?.index !== undefined) {
            return +element.dataset.index;
          }
          element = element.parentElement;
        }
        return null;
      };

      const anchorIndex: number =
        selection?.anchorNode?.parentElement === scrollRef.current
          ? Math.max(
              0,
              compensateForRemovedElementsPosition(selection.anchorOffset) - 2,
            )
          : (findDataIndex(selection.anchorNode) ??
            Number(
              selection.anchorNode?.parentElement?.dataset?.index ||
                (selection.anchorNode as HTMLElement)?.dataset?.index,
            ));
      const focusIndex: number =
        selection?.focusNode?.parentElement === scrollRef.current
          ? Math.max(
              0,
              compensateForRemovedElementsPosition(selection?.focusOffset) - 2,
            )
          : (findDataIndex(selection.focusNode) ??
            Number(
              selection?.focusNode?.parentElement?.dataset?.index ||
                (selection.focusNode as HTMLElement)?.dataset?.index,
            ));

      if (isNaN(anchorIndex) && isNaN(focusIndex)) return null;

      // When selecting left-to-right past the last element (e.g., including a
      // trailing period), the browser focus node can land on a container without
      // data-index.  Fall back to the last data-index child inside the range.
      const resolveNaN = (idx: number, otherIdx: number): number => {
        if (!isNaN(idx)) return idx;
        if (!selection.rangeCount) return otherIdx;
        const range = selection.getRangeAt(0);
        const container = scrollRef.current;
        if (!container) return otherIdx;
        const spans = container.querySelectorAll('[data-index]');
        let best = otherIdx;
        for (let i = spans.length - 1; i >= 0; i--) {
          const span = spans[i] as HTMLElement;
          if (range.intersectsNode(span) && span.dataset.index !== undefined) {
            best = +span.dataset.index;
            break;
          }
        }
        return best;
      };

      const resolvedAnchor = resolveNaN(anchorIndex, focusIndex);
      const resolvedFocus = resolveNaN(focusIndex, anchorIndex);
      if (isNaN(resolvedAnchor) || isNaN(resolvedFocus)) return null;

      let startIndex, endIndex, startOffset;
      if (resolvedAnchor >= resolvedFocus) {
        startIndex = resolvedFocus;
        endIndex = resolvedAnchor;
        startOffset = selection.focusOffset;
      } else {
        startIndex = resolvedAnchor;
        endIndex = resolvedFocus;
        startOffset = selection.anchorOffset;
      }

      if (selection?.toString()?.length) {
        if (
          transcriptElements &&
          transcriptElements[startIndex]?.type === 'punct' &&
          startOffset
        ) {
          startIndex += 1;
        }
      }

      return {
        startIndex,
        endIndex,
        focusIndex: resolvedFocus,
        anchorIndex: resolvedAnchor,
      };
    },
    [transcriptElements, compensateForRemovedElementsPosition],
  );

  const getBoundaryIndexesFromSelection = useCallback(
    (selection: Selection, scrollRef?: RefObject<HTMLDivElement>) => {
      if (showHistory) return getBoundaryIndexesFromSelectionInDiff(selection);
      else return getIndexBoundariesFromSelection(selection, scrollRef!);
    },
    [
      showHistory,
      getBoundaryIndexesFromSelectionInDiff,
      getIndexBoundariesFromSelection,
    ],
  );

  const getStartAndEndOfSelection = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      const selection = window.getSelection();
      if (!(selection && selection.toString().length)) return null;
      const boundaries = getBoundaryIndexesFromSelection(selection, scrollRef);
      if (!boundaries) {
        selection.empty();
      }
      return boundaries;
    },
    [getBoundaryIndexesFromSelection],
  );

  const convertIndexToSegmentBoundaries = useCallback(
    (indexBoundaries: SelectionBoundaries) => {
      let { startIndex, endIndex } = indexBoundaries;

      if (!transcriptElements) {
        return null;
      }

      // Look up transcript elements at start and end positions to get segment data
      let startElement = transcriptElements[startIndex];
      let endElement = transcriptElements[endIndex];

      if (!startElement || !endElement) {
        return null;
      }

      // If start element has whitespace subtype, find closest non-whitespace to the right
      if (startElement?.subtype === 'whitespace') {
        const nonWhitespaceStartIndex = getClosestElementIndexToRightByFilter(
          startIndex,
          transcriptElements,
          el => el.subtype !== 'whitespace',
        );

        if (nonWhitespaceStartIndex !== -1) {
          startIndex = nonWhitespaceStartIndex;
          startElement = transcriptElements[startIndex];
        }
      }

      // If end element has whitespace subtype, find closest non-whitespace to the left
      if (endElement?.subtype === 'whitespace') {
        const nonWhitespaceEndIndex = getClosestElementIndexToLeftByFilter(
          endIndex,
          transcriptElements,
          el => el.subtype !== 'whitespace',
        );

        if (nonWhitespaceEndIndex !== -1) {
          endIndex = nonWhitespaceEndIndex;
          endElement = transcriptElements[endIndex];
        }
      }

      // Validate that ranges are still valid after adjustment
      if (startIndex > endIndex) {
        logger.warn(
          `Invalid range after whitespace adjustment: startIndex (${startIndex}) > endIndex (${endIndex})`,
        );
        return null;
      }

      // Both elements must have segment data - clip_segment_id and clip_segment_position properties;
      if (
        !('clip_segment_id' in startElement) ||
        !('clip_segment_id' in endElement) ||
        !startElement.clip_segment_id ||
        !endElement.clip_segment_id ||
        startElement.clip_segment_position == null ||
        endElement.clip_segment_position == null
      ) {
        return null;
      }

      return {
        fromSegmentId: startElement.clip_segment_id,
        fromIndex: startElement.clip_segment_position,
        toSegmentId: endElement.clip_segment_id,
        toIndex: endElement.clip_segment_position,
      };
    },
    [transcriptElements],
  );

  // New: Segment-based boundary extraction for VideoEditor commands
  // Reuses proven getBoundaryIndexesFromSelection logic and converts indexes to segment data
  const getSegmentBoundariesFromSelection = useCallback(
    (
      selection: Selection,
      scrollRef: RefObject<HTMLDivElement>,
    ): {
      fromSegmentId: string;
      fromIndex: number;
      toSegmentId: string;
      toIndex: number;
    } | null => {
      // First, get the index-based boundaries using the proven logic
      // This correctly delegates to getBoundaryIndexesFromSelectionInDiff when showHistory is true
      const indexBoundaries = getBoundaryIndexesFromSelection(
        selection,
        scrollRef,
      );
      if (!indexBoundaries || !transcriptElements) {
        return null;
      }
      return convertIndexToSegmentBoundaries(indexBoundaries);
    },
    [
      getBoundaryIndexesFromSelection,
      convertIndexToSegmentBoundaries,
      transcriptElements,
    ],
  );

  const handleCursorMove = useCallback(
    (
      direction: 1 | -1,
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
    ) => {
      if (!transcriptElements) return;
      // Skip whitespace / punctuation elements when navigating with arrow
      // keys. Whitespace is a pass-through in the user's mental model:
      // arrows should always land on the next/prev word, not produce a
      // double-stop on the leading space + the word it precedes. Backspace
      // semantics follow naturally — a "before-word" cursor position no
      // longer exists, so backspace deletes the actual previous word.
      let newPosition;
      if (direction > 0) {
        newPosition = getClosestNotRemovedTextIndexToRight(
          cursor.from + 1,
          transcriptElements,
        );
        if (newPosition === -1) {
          newPosition =
            cursor.from + 1 > transcriptElements.length
              ? 0
              : transcriptElements.length;
        }
      } else {
        newPosition = getClosestNotRemovedTextIndexToLeft(
          cursor.from - 1,
          transcriptElements,
        );
        newPosition =
          newPosition === -1 ? transcriptElements.length : newPosition;
      }

      setCursor({
        from: newPosition || 0,
        to: (newPosition || 0) + 1,
      });

      const newPositionElement = transcriptElements[newPosition];

      if (
        newPositionElement &&
        'ts' in newPositionElement &&
        newPositionElement.ts != null
      ) {
        const moveToTs = Math.ceil((newPositionElement.ts || 0) * 1000) / 1000;
        videoCreator.setTime(moveToTs, true);
      }
    },
    [transcriptElements, videoCreator],
  );

  const handleAddPunctuation = useCallback(
    (
      code: 'Comma' | 'Period' | 'Space' | 'Enter',
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
    ) => {
      if (!transcriptElements) return;
      const punctAfterIndex = getClosestElementIndexToLeftByFilter(
        cursor.from - 1,
        transcriptElements,
        el =>
          el.subtype !== 'whitespace' &&
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          !!el.id,
      );
      if (punctAfterIndex === -1) {
        logger.error("handleAddPunctuation: couldn't find index to add after", {
          cursor,
        });
        return;
      }
      const punctAfterElement = transcriptElements[punctAfterIndex];
      if (
        !punctAfterElement ||
        punctAfterElement.clip_segment_id == null ||
        punctAfterElement.clip_segment_position == null
      ) {
        logger.error(
          "handleAddPunctuation: couldn't find element to add after",
          {
            cursor,
            punctAfterIndex,
            punctAfterElement,
          },
        );
        return;
      }
      editorStore.editor.commandAddTranscriptPunctuation(
        code,
        punctAfterElement.clip_segment_id,
        punctAfterElement.clip_segment_position,
      );
      if (code === 'Enter') {
        return; // don't change cursor because newline will take whitespace's place
      }
      setCursor({
        from: cursor.from + 1,
        to: cursor.from + 2,
      });
    },
    [videoCreator, transcriptElements],
  );

  /**
   * PLAT-11914 — the captions-view token a soft break before the word at
   * `index` hangs off (the nearest visible token to its left in the same
   * row), or -1 at a row start, where the caption already starts a line.
   * An opening bracket (`「`) is passed over: it opens the next line, so the
   * break ends the line before it (as `resolveSoftBreakToken` stores it).
   */
  const softBreakAnchorIndexBefore = useCallback(
    (index: number): number => {
      if (!transcriptElements) return -1;
      const lineId = transcriptElements[index]?.viewData?.lineId;
      if (!lineId) return -1;
      const anchorIndex = getClosestElementIndexToLeftByFilter(
        index - 1,
        transcriptElements,
        el =>
          !isWhitespaceElement(el) &&
          !isCjkOpeningPunctuation(el.value ?? '') &&
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          !!el.id,
      );
      return transcriptElements[anchorIndex]?.viewData?.lineId === lineId
        ? anchorIndex
        : -1;
    },
    [transcriptElements],
  );

  /** PLAT-11914 — add or remove a soft line break before the word at `index`. */
  const handleToggleCaptionSoftBreak = useCallback(
    (index: number) => {
      const anchorIndex = softBreakAnchorIndexBefore(index);
      const tokenId = transcriptElements?.[anchorIndex]?.id;
      if (anchorIndex === -1 || !tokenId) return;
      commandToggleCaptionSoftBreak({ tokenId });
    },
    [
      softBreakAnchorIndexBefore,
      transcriptElements,
      commandToggleCaptionSoftBreak,
    ],
  );

  const handleAddKaraokeBreak = useCallback(
    (cursor: { from: number; to: number }, soft = false) => {
      if (!transcriptElements) return;
      // PLAT-11914: Shift+Enter — a new line inside the same caption.
      if (soft) {
        handleToggleCaptionSoftBreak(cursor.from);
        return;
      }
      const breakAfterIndex = getClosestElementIndexToLeftByFilter(
        cursor.from - 1,
        transcriptElements,
        el =>
          el.subtype !== 'whitespace' &&
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          !!el.id,
      );
      if (breakAfterIndex === -1) return;
      const breakAfterElement = transcriptElements[breakAfterIndex];
      if (!breakAfterElement?.id) return;
      commandAddBreak(breakAfterElement.id);
      return;
    },
    [transcriptElements, videoCreator, handleToggleCaptionSoftBreak],
  );

  const handleRemoveKaraokeBreak = useCallback(
    (position: number) => {
      if (
        position < 0 ||
        !transcriptElements ||
        position > transcriptElements.length - 1
      )
        return;

      const restoreAfterIndex = getClosestElementIndexToLeftByFilter(
        position,
        transcriptElements,
        el =>
          el.subtype !== 'whitespace' &&
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          !!el.id,
      );
      if (restoreAfterIndex === -1) return;
      const restoreAfterElement = transcriptElements[restoreAfterIndex];
      if (!restoreAfterElement?.id) return;
      commandRemoveBreak(restoreAfterElement.id);
      return;
    },
    [transcriptElements, videoCreator],
  );

  const extendEndIndexToFollowingWhitespace = useCallback(
    (endIndex: number): number => {
      if (!transcriptElements) {
        return endIndex;
      }
      const nextElIndex = getClosestNotRemovedElementIndexToRight(
        endIndex + 1,
        transcriptElements,
      );
      if (nextElIndex !== -1 && transcriptElements[nextElIndex].value === ' ') {
        return nextElIndex;
      } else {
        return endIndex;
      }
    },
    [transcriptElements],
  );

  const handleTranscriptRestore = useCallback(
    async (
      action: 'restore' | 'showKaraoke',
      scrollRef?: RefObject<HTMLDivElement>,
    ) => {
      if (videoCreator.isPlaying) return;
      if (!transcriptElements) return;

      // Check if we should use new VideoEditor architecture
      // Note: editorStore is now passed as a parameter

      // LEGACY: Index-based path with VideoCreatorStore
      let startIndex, endIndex;
      const boundaries = getStartAndEndOfSelection(scrollRef);
      if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
        startIndex = getClosestRemovedIndexToRight(
          startIndex,
          transcriptElements,
        );
        endIndex = getClosestRemovedIndexToLeft(endIndex, transcriptElements);
        if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
          console.log(
            '[handleTranscriptRestore] Exiting early - invalid indices',
          );
          return;
        }
      } else if (selectedWordIndex > -1) {
        endIndex = startIndex = selectedWordIndex;
        if (
          action === 'showKaraoke' &&
          transcriptElements[startIndex].type === 'text'
        ) {
          endIndex = extendEndIndexToFollowingWhitespace(endIndex);
        }
      } else {
        return;
      }

      if (action === 'showKaraoke') {
        const tokenIds: string[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
          const el = transcriptElements[i];
          if (
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            el.id
          ) {
            tokenIds.push(el.id);
          }
        }
        if (tokenIds.length) {
          commandShowInKaraoke(tokenIds);
        }
        return;
      } else {
        // NEW: VideoEditor path with segment-based boundaries (Not for karaoke yet)
        const segmentBoundaries = convertIndexToSegmentBoundaries({
          startIndex,
          endIndex,
        });
        if (segmentBoundaries) {
          // Get natural media dimensions for aspect ratio-aware crop computation
          const naturalMediaDimensions =
            await videoCreator.getNaturalMediaDimensions();
          editorStore.editor.commandTranscriptRestore(
            segmentBoundaries.fromSegmentId,
            segmentBoundaries.fromIndex,
            segmentBoundaries.toSegmentId,
            segmentBoundaries.toIndex,
            naturalMediaDimensions,
            editorStore.sceneDetectionsForEdit(videoCreator.sceneDetections),
            videoCreator.faceDetections ?? undefined,
            editorStore.framingService,
          );
          videoCreator.recalculateTimelineScaleIfAtDefaultZoom();
          return;
        }
        return;
        // NEW: EARLY EXIT
      }
    },
    [
      transcriptElements,
      selectedWordIndex,
      videoCreator,
      getStartAndEndOfSelection,
      getSegmentBoundariesFromSelection,
      extendEndIndexToFollowingWhitespace,
    ],
  );

  const getSelectedStartEndIndex = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      let startIndex, endIndex;
      const override = selectionRangeOverrideRef?.current;
      const boundaries = override ? null : getStartAndEndOfSelection(scrollRef);
      if (override) {
        ({ startIndex, endIndex } = override);
      } else if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
      } else if (selectedWordIndex > -1) {
        endIndex = startIndex = selectedWordIndex;
      } else {
        return null;
      }

      startIndex = Math.max(startIndex, 0);
      endIndex = Math.min(endIndex, transcriptElements!.length - 1);
      return { startIndex, endIndex };
    },
    [
      getStartAndEndOfSelection,
      selectedWordIndex,
      transcriptElements,
      selectionRangeOverrideRef,
    ],
  );

  const handleHideKaraoke = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      if (!transcriptElements) return;
      let { startIndex, endIndex } = getSelectedStartEndIndex(scrollRef) || {};
      if (startIndex === undefined || endIndex === undefined) return;

      const tokenIds = [];
      for (let i = startIndex; i <= endIndex; i++) {
        const el = transcriptElements[i];
        if (
          el.subtype !== 'whitespace' &&
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          !!el.id
        ) {
          tokenIds.push(el.id);
        }
      }
      if (tokenIds.length) {
        commandHideInKaraoke(tokenIds);
      }
    },
    [
      transcriptElements,
      getSelectedStartEndIndex,
      extendEndIndexToFollowingWhitespace,
      videoCreator,
    ],
  );

  const handleTranscriptDelete = useCallback(
    (
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
      setLastTimeUpdated: (time: number) => void,
      scrollRef?: RefObject<HTMLDivElement>,
      isInKaraokeView?: boolean,
      toClipboard: boolean = false,
    ) => {
      if (videoCreator.isPlaying) return;
      if (!transcriptElements) return;

      // Note: editorStore is now passed as a parameter
      const editor = editorStore?.editor;

      let startIndex: number, endIndex: number;
      const boundaries = getStartAndEndOfSelection(scrollRef);
      if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
      } else if (selectedWordIndex > 0) {
        endIndex = startIndex = getClosestNotRemovedElementIndexToLeft(
          selectedWordIndex,
          transcriptElements,
        );
      } else if (cursor.to > 0) {
        // PLAT-11703 — in the captions view, Backspace at the start of a
        // caption row joins it to the row above rather than deleting a word.
        // That gesture used to fall out of the walk below: a row's trailing
        // space is synthesized per line and carries no segment address, so
        // `convertIndexToSegmentBoundaries` returned null and the
        // no-boundaries branch removed the break. PLAT-11558 taught the walk
        // to skip whitespace — correct for a mid-row Backspace, but it lands
        // on the last word of the row above and deletes it. So the row
        // boundary has to be recognised here, not fall out of a null.
        // PLAT-11914 — at the start of a soft line, remove the soft break;
        // the caption is not joined to anything.
        const softAnchor = isInKaraokeView
          ? softBreakAnchorIndexBefore(cursor.from)
          : -1;
        if (transcriptElements[softAnchor]?.viewData?.softBreakAfter) {
          handleToggleCaptionSoftBreak(cursor.from);
          setCursor({ from: cursor.from, to: cursor.to });
          return;
        }
        if (
          isInKaraokeView &&
          isCaptionRowStart(transcriptElements, cursor.from)
        ) {
          handleRemoveKaraokeBreak(cursor.from - 1);
          // Hold the cursor where it is. Joining two rows deletes no token —
          // the row above's synthetic trailing space is simply replaced by the
          // ordinary space between the two words — so the word under the
          // cursor keeps its index. Walking the cursor left here (as the old
          // no-boundaries branch did, because it had just deleted something)
          // put it a word behind the join and scrubbed the video back with it.
          // Re-setting to the current position also defeats the cursor-recalc
          // effect, exactly as the whitespace-delete case below does.
          setCursor({ from: cursor.from, to: cursor.to });
          return;
        }
        // Skip whitespace: the cursor only lands on words (PLAT-9636), so
        // `cursor.from - 1` is always a space, and a synthesized space has no
        // segment address to delete by — resolving to one made
        // `convertIndexToSegmentBoundaries` return null (PLAT-11558).
        endIndex = startIndex = getClosestElementIndexToLeftByFilter(
          cursor.from - 1,
          transcriptElements,
          el => {
            return (
              el.state !== 'removed' &&
              el.state !== 'cut' &&
              el.subtype !== 'whitespace' &&
              !!el.value &&
              (el.karaoke_break || !isInKaraokeView || el.value !== '\n')
            );
          },
        );
      } else {
        return;
      }

      // NEW: VideoEditor path with segment-based boundaries
      // Convert legacy index-based to segment-based boundaries
      const selection = window.getSelection();
      if (selection) {
        const segmentBoundaries = convertIndexToSegmentBoundaries({
          startIndex,
          endIndex,
        });
        if (segmentBoundaries) {
          editor.commandRemoveTranscript(
            segmentBoundaries.fromSegmentId,
            segmentBoundaries.fromIndex,
            segmentBoundaries.toSegmentId,
            segmentBoundaries.toIndex,
            toClipboard,
          );
          // this prevents cursor from jumping to the next line of karaoke on backspace
          if (startIndex === endIndex && isInKaraokeView) {
            if (transcriptElements[startIndex].type === 'text') {
              // Deleted a word — land cursor on the word before the deleted one.
              handleCursorMove(
                -1,
                { ...cursor, from: cursor.from - 1 },
                setCursor,
              );
            } else {
              // Deleted whitespace at a karaoke-break boundary (Enter+Backspace
              // round-trip). The next word is still at cursor.from since
              // state='removed' doesn't shift indices — re-set the cursor to
              // its current position to defeat the cursor-recalc effect that
              // would otherwise move it.
              setCursor({ from: cursor.from, to: cursor.to });
            }
          } else if (
            startIndex === endIndex &&
            startIndex < cursor.from &&
            transcriptElements[startIndex].type === 'punct' &&
            transcriptElements[startIndex].subtype !== 'whitespace'
          ) {
            // Removing punctuation DROPS the token (punctuation.ts
            // `removePunctuationRange`) rather than marking it removed the way
            // a word is, so every later index shifts left by one — follow it,
            // or the cursor lands a word to the right and the next Backspace
            // eats the wrong word (PLAT-11558).
            setCursor({ from: cursor.from - 1, to: cursor.to - 1 });
          }
          // VideoEditor handles timeline updates internally
          return;
        } else if (startIndex === endIndex && startIndex > 0) {
          if (isInKaraokeView) handleRemoveKaraokeBreak(startIndex);
          handleCursorMove(-1, cursor, setCursor);
          return;
        }
      }
    },
    [
      transcriptElements,
      selectedWordIndex,
      videoCreator,
      getStartAndEndOfSelection,
      handleRemoveKaraokeBreak,
      softBreakAnchorIndexBefore,
      handleToggleCaptionSoftBreak,
    ],
  );

  const getCursorPosition = useCallback(() => {
    const currentWordIndex = transcriptElements!.findIndex(
      el =>
        el.type === 'text' &&
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        el.value &&
        'end_ts' in el &&
        el.end_ts &&
        el.end_ts > videoCreator.time,
    );

    return {
      from: currentWordIndex,
      to: currentWordIndex + 1,
    };
  }, [transcriptElements, videoCreator.time]);

  const skipToWordWithIndex = useCallback(
    (
      index: number,
      setCursor: (cursor: { from: number; to: number }) => void,
      setAutoScroll: (autoScroll: boolean) => void,
    ) => {
      if (!transcriptElements?.at(index)) return;
      const wasPlaying = videoCreator.isPlaying;
      let element = transcriptElements[index];
      // For removed/cut/punct elements, find the closest visible text element to seek to
      if (
        element.type !== 'text' ||
        element.state === 'removed' ||
        element.state === 'cut'
      ) {
        element =
          transcriptElements[
            getClosestNotRemovedTextIndexToRight(index, transcriptElements)
          ] ??
          transcriptElements[
            getClosestNotRemovedTextIndexToLeft(index, transcriptElements)
          ];
        if (!element) return;
      }

      if ('ts' in element && element.ts != null) {
        const seekTime = element.ts;
        // Dispatch transcript-seek (sole listener: TranscriptVideoPlayer,
        // whose RAF feedback prevention blocks the MobX reaction during
        // playback). Scoped to OUR videoCreator so review-pane clicks never
        // move players on other surfaces (PLAT-10286).
        dispatchTranscriptSeek(seekTime, videoCreator);
        videoCreator.setTime(seekTime, true).then(() => {
          // Don't resume the renderer when an external player owns playback
          // (the Translation Review compact player) — it would be a second
          // audio source (PLAT-10286).
          if (wasPlaying && !videoCreator.suppressRendererPlayback) {
            videoCreator.renderer?.play();
          }
          setCursor({
            from: index,
            to: index + 1,
          });
          setAutoScroll(wasPlaying);
        });
      }
    },
    [transcriptElements, videoCreator],
  );

  const getActions = useCallback(
    (
      wordIndex: number,
      selection: Selection | null = null,
      scrollRef?: RefObject<HTMLDivElement>,
    ): TranscriptContextMenuBuild => {
      let clipboardNotEmpty = false;
      // Note: editorStore is now passed as a parameter
      const editor = editorStore?.editor;

      clipboardNotEmpty = editor.getClipboardEntries().length > 0;

      if (wordIndex >= transcriptElements!.length) {
        if (clipboardNotEmpty) {
          return { actions: ['pasteTextBefore'], disabledReasons: {} };
        }
        return { actions: [], disabledReasons: {} };
      }
      const selectedItem = transcriptElements![wordIndex];

      if (selection && selection.toString().length) {
        const { startIndex, endIndex } =
          getBoundaryIndexesFromSelection(selection, scrollRef) || {};
        if (
          showHistory &&
          startIndex !== undefined &&
          endIndex !== undefined &&
          transcriptElements!
            .slice(startIndex, endIndex + 1)
            .some(el => el.state === 'removed' || el.state === 'cut')
        ) {
          return { actions: ['restore'], disabledReasons: {} };
        }
      }

      if (selectedItem.state === 'removed' || selectedItem.state === 'cut')
        return { actions: ['restore'], disabledReasons: {} };
      const menuActions = [] as TranscriptContextMenuActions[];
      const disabledReasons: TranscriptContextMenuDisabledReasons = {};
      // PLAT-11601 — Replace Term is always listed; past the word limit it is
      // disabled with the limit explained instead of vanishing. Whitespace runs
      // count as one separator, so a double or trailing space can't eat a word.
      const selectedWordCount = countSelectedWords(selection);
      menuActions.push('replace');
      if (selectedWordCount > REPLACE_TERM_WORD_LIMIT) {
        disabledReasons.replace = REPLACE_TERM_LIMIT_REASON;
      }
      // Paste keeps its historical gate (clipboard + within the limit); the
      // PLAT-11598 audit tracks decoupling it as a separate gap.
      if (selectedWordCount <= REPLACE_TERM_WORD_LIMIT && clipboardNotEmpty) {
        menuActions.push('pasteTextBefore');
      }

      // PLAT-11914 — captions view: a line break before this word, unless it
      // already starts the caption.
      const softAnchor = softBreakAnchorIndexBefore(wordIndex);
      if (softAnchor !== -1) {
        menuActions.push(
          transcriptElements?.[softAnchor]?.viewData?.softBreakAfter
            ? 'removeLineBreak'
            : 'addLineBreak',
        );
      }

      if (hasKaraokeLines) {
        const { id } = selectedItem;
        menuActions.push(
          karaokeLines &&
            id &&
            karaokeLines.some(line =>
              line?.allHiddenTranscriptTokenIds?.includes(id),
            )
            ? 'showKaraoke'
            : 'hideKaraoke',
        );
      }

      if (
        videoCreator.punchListManager.findPunchListItemByTranscriptPosition(
          transcriptElements?.[wordIndex].viewData?.finalTranscriptIndex ??
            wordIndex,
        )
      ) {
        const photoActions: TranscriptContextMenuActions[] = videoCreator
          .punchListManager.addedPunchListItemId
          ? [
              ...menuActions,
              'removeTextLoading',
              'removePhotoLoading',
              'cutText',
              'saveAsClip',
              'saveSegmentForLater',
              'saveAsPullQuote',
            ]
          : [
              ...menuActions,
              'removeText',
              'removePhoto',
              'cutText',
              'saveAsClip',
              'saveSegmentForLater',
              'saveAsPullQuote',
            ];
        return { actions: photoActions, disabledReasons };
      }

      // Check if any selected text element has been reassigned (has original_speaker_label)
      const hasReassignedSpeaker = (() => {
        if (selection && selection.toString().length) {
          const boundaries = getBoundaryIndexesFromSelection(
            selection,
            scrollRef,
          );
          if (boundaries && transcriptElements) {
            return transcriptElements
              .slice(boundaries.startIndex, boundaries.endIndex + 1)
              .some(isElementReassigned);
          }
        }
        return isElementReassigned(transcriptElements![wordIndex]);
      })();

      const finalActions = [
        ...menuActions,
        'removeText',
        'cutText',
        'saveAsClip',
        'saveSegmentForLater',
        'saveAsPullQuote',
        'reassignSpeaker',
        ...(hasReassignedSpeaker ? ['undoReassignSpeaker'] : []),
        videoCreator.punchListManager.addedPunchListItemId
          ? 'addPhotoLoading'
          : 'addPhoto',
        // PLAT-10105 — overlay-graphic insert from a transcript selection.
        // Surfaced only on clip preview; other surfaces filter it out via
        // `hideAddOverlayGraphic` (and Transcript drops it when no
        // `onAddOverlayGraphicRequested` handler is wired).
        'addOverlayGraphic',
      ];

      return {
        actions: finalActions as TranscriptContextMenuActions[],
        disabledReasons,
      };
    },
    [
      transcriptElements,
      videoCreator,
      getBoundaryIndexesFromSelection,
      showHistory,
      softBreakAnchorIndexBefore,
    ],
  );

  const handleRemovePhoto = useCallback(async () => {
    if (videoCreator.punchListManager.addedPunchListItemId) return;
    const punchListId =
      videoCreator.punchListManager.findPunchListItemByTranscriptPosition(
        transcriptElements?.[selectedWordIndex].viewData
          ?.finalTranscriptIndex ?? selectedWordIndex,
      )?.id;
    if (!punchListId) return;
    videoCreator.punchListManager.removePunchListItem(punchListId);
    editorStore.editor.commandRemoveElement(punchListId);
  }, [transcriptElements, selectedWordIndex, videoCreator]);

  const handleAddPhoto = useCallback(
    async (
      photoSelection: {
        loc: Record<'startIndex' | 'endIndex', number>;
        text: string;
      },
      selectedImage: ImageWithType[ImageKey],
    ) => {
      if (!photoSelection || !selectedImage?.url) {
        return;
      }
      if (!photoSelection.loc) return;

      let startIndex = getClosestNotRemovedTextIndexToRight(
        photoSelection.loc.startIndex,
        transcriptElements!,
      );

      let endIndex = getClosestNotRemovedTextIndexToLeft(
        photoSelection.loc.endIndex,
        transcriptElements!,
      );
      if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) return;

      const startElement = transcriptElements?.at(startIndex);
      const endElement = transcriptElements?.at(endIndex);

      if (
        !startElement ||
        !endElement ||
        !('ts' in startElement) ||
        !('end_ts' in endElement)
      )
        return;

      const startTime = startElement.ts!;
      const endTime = endElement.end_ts!;
      videoCreator.sidebarOptions = SidebarOption.aiProducer;

      const aspectRatio = +selectedImage.width / +selectedImage.height;

      const punchListItem = videoCreator.punchListManager.addPhotoToPunchList(
        photoSelection.text,
        selectedImage.url,
        selectedImage.description || '',
        selectedImage.type,
        startElement.viewData?.finalTranscriptIndex ?? startIndex,
        endElement.viewData?.finalTranscriptIndex ?? endIndex,
        aspectRatio,
      );
      try {
        const duration = endTime - startTime;
        const maxTrack = videoCreator.getMaxTrack();
        const currTrack = maxTrack + 1;
        const freeTrack = videoCreator.getFreeMediaTrack(
          'image',
          duration,
          startTime,
        );
        const punchListTrack = freeTrack || currTrack;
        const isPortrait = aspectRatio < 1;
        const videoAspectRatio =
          videoCreator.currentVideo?.aspectRatio ||
          videoCreator.getDefaultAspectRatio();

        if (!mediaService) return;
        await mediaService.addPhotoToTimeline({
          item: {
            id: punchListItem!.id!,
            src: punchListItem!.artifactSrc!,
            description: selectedImage.description || '',
            aspectRatio,
            alt: selectedImage.alt || '',
            type: punchListItem.type as 'artifact' | 'stock' | 'ai',
          },
          time: startTime,
          duration,
          track: punchListTrack,
          useComposition:
            isPortrait && videoAspectRatio === AspectRatio.AR_16_9,
          id: punchListItem!.id!,
        });
      } catch (error) {
        logger.error(
          'Error adding photo to timeline:',
          extractErrorInfo(error),
        );
        videoCreator.punchListManager.removePunchListItem(punchListItem!.id!);
      }

      setSelectedWordIndex(-1);
    },
    [transcriptElements, videoCreator, mediaService],
  );

  // PLAT-10105 — resolve a transcript selection's `loc` (start/end element
  // indices) to timeline timestamps, reusing the SAME removed-element-skip
  // boundary logic `handleAddPhoto` applies (closest non-removed element to
  // the right for the start, to the left for the end). Returns null when the
  // range can't be resolved (all-removed selection, missing timestamps).
  const resolveSelectionTimeRange = useCallback(
    (
      loc: Record<'startIndex' | 'endIndex', number>,
    ): {
      startTime: number;
      endTime: number;
    } | null => {
      if (!transcriptElements) return null;
      const startIndex = getClosestNotRemovedTextIndexToRight(
        loc.startIndex,
        transcriptElements,
      );
      const endIndex = getClosestNotRemovedTextIndexToLeft(
        loc.endIndex,
        transcriptElements,
      );
      if (startIndex === -1 || endIndex === -1 || endIndex < startIndex)
        return null;

      const startElement = transcriptElements.at(startIndex);
      const endElement = transcriptElements.at(endIndex);
      if (
        !startElement ||
        !endElement ||
        !('ts' in startElement) ||
        !('end_ts' in endElement)
      )
        return null;

      const startTime = startElement.ts;
      const endTime = endElement.end_ts;
      if (
        typeof startTime !== 'number' ||
        typeof endTime !== 'number' ||
        endTime <= startTime
      )
        return null;

      return { startTime, endTime };
    },
    [transcriptElements],
  );

  /**
   * CANONICAL transcript-selection resolver. Every selection-driven action
   * (overlay, remove/cut, save-as-clip, pull-quote, photo, reassign speaker)
   * should resolve "what is selected" through this one path: DOM selection →
   * raw indices (`getBoundaryIndexesFromSelection`) → normalize to real words
   * (`normalizeWordBoundaries`, skips whitespace/removed) → derive text +
   * time range. Actions then map the result to their own needs instead of
   * re-deriving boundaries (which is how overlay vs remove drifted apart).
   */
  const resolveTranscriptSelection = useCallback(
    (
      selection: Selection | null,
      scrollRef: RefObject<HTMLDivElement>,
      opts?: { includeRemoved?: boolean },
    ): ResolvedTranscriptSelection | null => {
      if (!transcriptElements) return null;
      // Programmatic override (translated context menu) wins over the DOM
      // selection — the translated DOM has no data-index spans to resolve.
      const raw =
        selectionRangeOverrideRef?.current ??
        (selection
          ? getBoundaryIndexesFromSelection(selection, scrollRef)
          : null);
      if (!raw) return null;
      const norm = normalizeWordBoundaries(
        raw.startIndex,
        raw.endIndex,
        transcriptElements,
        opts,
      );
      if (!norm) return null;

      const { startIndex, endIndex } = norm;
      const startEl = transcriptElements[startIndex] as {
        ts?: number;
        value?: string;
      };
      const endEl = transcriptElements[endIndex] as {
        end_ts?: number;
        value?: string;
      };
      const startTime = startEl?.ts;
      const endTime = endEl?.end_ts;
      if (
        typeof startTime !== 'number' ||
        typeof endTime !== 'number' ||
        endTime <= startTime
      ) {
        return null;
      }

      const text = transcriptElements
        .slice(startIndex, endIndex + 1)
        .map(el => (el as { value?: string }).value ?? '')
        .join('');

      return { startIndex, endIndex, startTime, endTime, text };
    },
    [
      transcriptElements,
      getBoundaryIndexesFromSelection,
      selectionRangeOverrideRef,
    ],
  );

  const handleCutText = useCallback(
    async (
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
      setLastTimeUpdated: (time: number) => void,
      scrollRef?: RefObject<HTMLDivElement>,
      isInKaraokeView?: boolean,
    ) => {
      handleTranscriptDelete(
        cursor,
        setCursor,
        setLastTimeUpdated,
        scrollRef,
        isInKaraokeView,
        true,
      );

      videoCreator.recalculateTimelineScale();
    },
    [getSelectedStartEndIndex, videoCreator],
  );

  const handlePasteText = useCallback(
    (after: boolean, scrollRef?: RefObject<HTMLDivElement>) => {
      const { startIndex, endIndex } =
        getSelectedStartEndIndex(scrollRef) || {};
      if (startIndex === undefined || endIndex === undefined) return;

      // Check if we should use new VideoEditor architecture
      // Note: editorStore is now passed as a parameter
      const editor = editorStore?.editor;

      const segmentBoundaries = convertIndexToSegmentBoundaries({
        startIndex: startIndex,
        endIndex: startIndex,
      });
      if (segmentBoundaries) {
        editor.commandPasteTranscript(
          segmentBoundaries?.fromSegmentId,
          segmentBoundaries?.fromIndex,
          after,
        );
      }
      return;
    },
    [getSelectedStartEndIndex, videoCreator],
  );

  const handleSaveAsClip = useCallback(
    async (
      scrollRef?: RefObject<HTMLDivElement>,
      onOptimistic?: (range: { startIndex: number; endIndex: number }) => void,
      onOpenClip?: (clipId: string) => void,
    ): Promise<boolean> => {
      // The DOM-selection gate only applies when no programmatic override
      // is set — the translated view dispatches with a mapped range and
      // no data-index DOM selection (PLAT-10646).
      if (!selectionRangeOverrideRef?.current) {
        const selection = window.getSelection();
        if (!selection || !selection.toString().length) return false;
      }
      const { startIndex, endIndex } =
        getSelectedStartEndIndex(scrollRef) || {};

      if (startIndex == null || endIndex == null) return false;
      if (!clipSaveService) return false;

      onOptimistic?.({ startIndex, endIndex });

      // Up the moment the save resolves the only feedback is the optimistic
      // highlight, which reads as "nothing happened" on a slow save — so the
      // toast goes up now and is swapped for its outcome in place.
      const toastId = videoCreator.showToast({
        state: 'pending',
        message: 'Saving your clip…',
      });

      try {
        const saved = await clipSaveService.saveClip({
          startIndex,
          endIndex,
          relevantElements: transcriptElements,
        });

        if (!saved) {
          throw new Error('Clip save completed without a saved video');
        }

        videoCreator.updateToast(toastId, {
          state: 'success',
          message: `Your clip "${saved.title}" has been saved`,
          // No explicit delay: `resolveToastDelay` gives a toast carrying an
          // action the longer actionable window, which is the point of the
          // action (PLAT-11752).
          ...(onOpenClip && {
            action: {
              label: 'Open Clip',
              onClick: () => {
                onOpenClip(saved.id);
              },
            },
          }),
        });
        return true;
      } catch (error) {
        logger.error('handleSaveAsClip:failed', extractErrorInfo(error));
        videoCreator.updateToast(toastId, {
          state: 'error',
          message: 'Failed to save clip. Please try again.',
        });
        return false;
      }
    },
    [
      getSelectedStartEndIndex,
      clipSaveService,
      videoCreator,
      transcriptElements,
    ],
  );

  const handleTranscriptTextReplace = useCallback(
    (
      selections: {
        startIndex: number;
        endIndex: number;
        newValue: string;
        retainState?: boolean;
      }[],
    ) => {
      if (!selections.length) return;

      type SingleSegChange = {
        fromIndex: number;
        toIndex: number;
        newText: string;
      };
      const singleSegmentGroups: Record<string, SingleSegChange[]> = {};
      const multiSegmentSelections: {
        fromSegmentId: string;
        fromIndex: number;
        toSegmentId: string;
        toIndex: number;
        newText: string;
      }[] = [];

      for (const sel of selections) {
        const b = convertIndexToSegmentBoundaries(sel);
        if (!b) {
          logger.warn('handleTranscriptTextReplace: boundary not found', sel);
          continue;
        }
        if (b.fromSegmentId === b.toSegmentId) {
          const arr = singleSegmentGroups[b.fromSegmentId] || [];
          arr.push({
            fromIndex: b.fromIndex,
            toIndex: b.toIndex,
            newText: sel.newValue,
          });
          singleSegmentGroups[b.fromSegmentId] = arr;
        } else {
          multiSegmentSelections.push({
            fromSegmentId: b.fromSegmentId,
            fromIndex: b.fromIndex,
            toSegmentId: b.toSegmentId,
            toIndex: b.toIndex,
            newText: sel.newValue,
          });
        }
      }

      for (const [segmentId, changes] of Object.entries(singleSegmentGroups)) {
        if (!changes.length) continue;
        if (changes.length === 1) {
          const c = changes[0];
          editorStore.editor.commandReplaceTranscriptText(
            segmentId,
            c.fromIndex,
            segmentId,
            c.toIndex,
            c.newText,
          );
          continue;
        }
        // Delegate all validation (ordering, overlap) to multi-range command.
        const ok = editorStore.editor.commandReplaceTranscriptTextMultiRanges(
          segmentId,
          changes,
        );
        if (!ok) {
          logger.warn(
            'handleTranscriptTextReplace: multi-range command failed due to validation',
            {
              segmentId,
              changes,
            },
          );
          // Fallback: sequential single-range commands (partial success acceptable).
          for (const c of changes) {
            editorStore.editor.commandReplaceTranscriptText(
              segmentId,
              c.fromIndex,
              segmentId,
              c.toIndex,
              c.newText,
            );
          }
        }
      }

      // Process multi-segment selections
      for (const m of multiSegmentSelections) {
        editorStore.editor.commandReplaceTranscriptText(
          m.fromSegmentId,
          m.fromIndex,
          m.toSegmentId,
          m.toIndex,
          m.newText,
        );
      }
    },
    [convertIndexToSegmentBoundaries],
  );

  return {
    selectedWordIndex,
    setSelectedWordIndex,
    compensateForRemovedElementsPosition,
    getBoundaryIndexesFromSelection,
    getStartAndEndOfSelection,
    handleCursorMove,
    handleAddPunctuation,
    handleAddKaraokeBreak,
    handleRemoveKaraokeBreak,
    handleToggleCaptionSoftBreak,
    handleTranscriptRestore,
    getSelectedStartEndIndex,
    handleHideKaraoke,
    handleTranscriptDelete,
    getCursorPosition,
    skipToWordWithIndex,
    getActions,
    handleRemovePhoto,
    handleAddPhoto,
    resolveSelectionTimeRange,
    resolveTranscriptSelection,
    handleCutText,
    handlePasteText,
    handleSaveAsClip,
    handleTranscriptTextReplace,
    convertIndexToSegmentBoundaries,
  };
};
